import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    CurrentPersonDto,
    DashboardService,
    FlyFreelyConstants,
    FlyFreelyError,
    FlyFreelyLoggingService,
    HubspotService,
    NotFound,
    OnboardingService,
    PersonsOrganisationDto,
    PreferencesService,
    UserService,
    UserStatus,
    hasAnyPermission
} from '@flyfreely-portal-ui/flyfreely';
import { AvailableWidgets } from '@flyfreely-portal-ui/widget';
import {
    SelectedOrganisation,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import { DynamicHelpEditService } from 'libs/enhanced-help/src/lib/dynamic-help-editor/dynamic-help-edit.service';
import { NotificationDialoguesService } from 'libs/notifications/src/lib/notifications-dialogues.service';
import { SetupGuideChecklistService } from 'libs/onboarding/src/lib/setup-guide/setup-guide-checklist.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subject, combineLatest } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    take,
    takeUntil
} from 'rxjs/operators';

@Component({
    selector: 'workspace',
    templateUrl: './workspace.component.html',
    styleUrls: ['./workspace.scss']
})
export class WorkSpaceComponent implements OnInit, OnDestroy {
    organisationId: number;

    scrollToListener: any;
    selectedWidgets: AvailableWidgets;
    // onChangeOrganisation: any;
    availableWidgets: any;

    systemNotice?: string;

    showPrivacySettings = false;

    showDynamicHelpEdit$: Observable<boolean>;

    showAlternativeSupport = false;
    alternativeSupportUrl?: SafeResourceUrl;

    canAdminOrganisation = false;

    showNavigation = false;

    currentUser: CurrentPersonDto | undefined;
    currentOrganisation: SelectedOrganisation = { type: 'loading' };
    currentOrganisationLoaded = false;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private userService: UserService,
        protected sharedService: WorkspaceStateService,
        private hubspotService: HubspotService,
        dynamicHelpEditService: DynamicHelpEditService,
        private notificationDialoguesService: NotificationDialoguesService,
        private commonDialoguesService: CommonDialoguesService,
        private setupGuideChecklistService: SetupGuideChecklistService,
        private deviceDetectorService: DeviceDetectorService,
        sanitizer: DomSanitizer,
        constants: FlyFreelyConstants,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private dashboardService: DashboardService,
        private onboardingService: OnboardingService,
        private preferencesService: PreferencesService,
        private logging: FlyFreelyLoggingService
    ) {
        this.alternativeSupportUrl =
            constants.ALTERNATIVE_SUPPORT_URL != null &&
            constants.ALTERNATIVE_SUPPORT_URL.length > 0
                ? sanitizer.bypassSecurityTrustResourceUrl(
                      constants.ALTERNATIVE_SUPPORT_URL
                  )
                : null;

        this.showDynamicHelpEdit$ = dynamicHelpEditService.editMode$;
        this.preferencesService.userDataPrivacyOptions$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                settings =>
                    (this.showPrivacySettings =
                        settings == null ||
                        settings.allowMixpanel == null ||
                        settings.allowSentry == null)
            );
    }

    ngOnInit() {
        combineLatest([
            this.userService.userChange$.pipe(
                filter(c => c.type === UserStatus.LOGGED_IN)
            ),
            this.activatedRoute.queryParams
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([user, params]) => {
                if (params?.organisationId == null) {
                    throw new Error('Arrived without an organisation ID');
                }
                this.organisationId = parseInt(params.organisationId);
                this.sharedService.changeOrganisation(this.organisationId);
            });

        if (this.alternativeSupportUrl == null) {
            this.showAlternativeSupport = false;
            this.hubspotService.showHubspotConversations();
        } else {
            combineLatest([
                this.hubspotService.hubspotConversationAvailable$,
                this.activatedRoute.queryParams
            ])
                .pipe(
                    map(([available, params]) => {
                        // `OrganisationRedirectGuard` derives `hsmode` from the organisation and
                        // writes it to the URL, which is also where HubSpot reads it from to pick
                        // the widget. Anything unset or unparseable falls back to no widget.
                        const hsmode = parseInt(params.hsmode, 10);
                        return available && !isNaN(hsmode) ? hsmode : 0;
                    }),
                    distinctUntilChanged(),
                    takeUntil(this.ngUnsubscribe$)
                )
                .subscribe(hsmode => {
                    if (hsmode === 0) {
                        this.showAlternativeSupport = true;
                        this.hubspotService.hideHubspotConversations();
                        return;
                    }
                    this.showAlternativeSupport = false;

                    this.hubspotService.showHubspotConversations();
                });
        }

        combineLatest([
            this.userService.userChange$.pipe(
                filter(c => c.type === UserStatus.LOGGED_IN)
            ),
            this.sharedService.currentOrganisation$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([changes, organisation]) => {
                this.currentOrganisationLoaded =
                    organisation.type !== 'loading';
                this.currentOrganisation = organisation;
                this.currentUser = changes.currentUser;

                this.canAdminOrganisation =
                    organisation.type === 'organisation_loaded' &&
                    !organisation.organisation.personalOrganisation &&
                    (hasAnyPermission(organisation.organisation, [
                        PersonsOrganisationDto.Permissions.ORGANISATION_EDIT,
                        PersonsOrganisationDto.Permissions
                            .FLIGHT_OPERATIONS_CONFIGURATION_VIEW
                    ]) ||
                        organisation.organisation.owner);

                this.showNavigation =
                    organisation.type === 'organisation_loaded';

                if (organisation.type === 'organisation_loaded') {
                    this.maybeShowMobileWarning();
                }
            });
    }

    private maybeShowWhatsNew() {
        this.userService
            .findLatestWhatsNew()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: whatsNew => {
                    if (whatsNew.changes.length > 0) {
                        const whatsNewModal =
                            this.notificationDialoguesService.showWhatsNew(
                                whatsNew
                            );

                        whatsNewModal.onHidden
                            .pipe(take(1))
                            .subscribe(() =>
                                this.setupGuideChecklistService.readyToShowStartHere()
                            );
                    } else {
                        this.setupGuideChecklistService.readyToShowStartHere();
                    }
                    this.systemNotice = whatsNew.systemNotice;
                },
                error: (error: FlyFreelyError) => {
                    this.setupGuideChecklistService.readyToShowStartHere();
                    if (error instanceof NotFound) {
                        // do nothing
                    }
                }
            });
    }

    private maybeShowMobileWarning() {
        // Ensure the mobile warning modal is shown first before checking what's new
        if (
            this.deviceDetectorService.isMobile() ||
            (this.deviceDetectorService.isTablet() &&
                this.deviceDetectorService.orientation.toLowerCase() ===
                    'portrait')
        ) {
            const modal = this.notificationDialoguesService.showMobileWarning();
            modal.onHidden
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(() => this.maybeShowWhatsNew());
        } else {
            this.maybeShowWhatsNew();
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    logout() {
        this.userService.logout();
    }

    showAllWhatsNew() {
        this.notificationDialoguesService.showAllWhatsNew();
    }

    showCreateCommercialOrganisation() {
        if (
            this.currentOrganisation.type === 'no_access' ||
            this.currentOrganisation.type === 'loading' ||
            this.currentOrganisation.type === 'no_organisation'
        ) {
            this.dashboardService.shouldReturnToDashboard(true);
        } else {
            this.dashboardService.shouldReturnToDashboard(
                true,
                this.currentOrganisation.organisation.id
            );
        }
        this.router.navigate(['onboarding']);
    }

    requestAccess() {
        this.onboardingService
            .requestOrganisationAccess()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.logging.success('Organisation access requested');
            });
    }

    changeWidgetSelection(updatedSelection: any) {
        this.selectedWidgets = { ...updatedSelection };
    }

    scrollTo(widgetId: number) {
        if (!this.scrollToListener) {
            return;
        }
        this.scrollToListener(widgetId);
    }

    registerScrollToListener(listener: any) {
        this.scrollToListener = listener;
    }

    removeScrollToListener() {
        this.scrollToListener = null;
    }

    showOrgAdmin() {
        this.commonDialoguesService.showConfirmationDialogue(
            'Organisation Admin Has Moved',
            'To access organisation admin, use the cog icon on the main navigation bar',
            'Got it',
            () => Promise.resolve()
        );
    }
}
